// @ts-nocheck
(() => {
  const COMPONENT_NAME = 'spx-klaviyo-api';
  const API_REVISION = '2026-07-15';

  class SpxKlaviyoApi extends HTMLElement {
    #form;
    #emailInput;
    #submitButton;
    #status;
    #abortController;

    connectedCallback() {
      this.#abortController?.abort();
      this.#abortController = new AbortController();
      this.#form = this.querySelector('form');
      this.#emailInput = this.querySelector('input[type="email"]');
      this.#submitButton = this.querySelector('button[type="submit"]');
      this.#status = this.querySelector('[data-klaviyo-status]');

      if (!(this.#form instanceof HTMLFormElement) || !(this.#emailInput instanceof HTMLInputElement)) {
        console.error('SpxKlaviyoApi: 缺少 form 或 email input。');
        return;
      }

      this.#form.addEventListener('submit', this.#handleSubmit, {
        signal: this.#abortController.signal,
      });

      this.#initModeSwitch();
    }

    // 分段控制器（可选）：存在 [data-klaviyo-mode] 按钮时启用双模式切换。
    // 每个按钮可带：data-mode-variant（有值=订阅后跳转结账/空=仅订阅）、
    // data-mode-source、data-mode-button-label。
    // 联动面板用 [data-klaviyo-panel="<mode>"]。
    #initModeSwitch() {
      const modeButtons = Array.from(this.querySelectorAll('[data-klaviyo-mode]'));
      if (modeButtons.length === 0) return;

      const activateMode = (mode) => {
        const button = modeButtons.find((b) => b.dataset.klaviyoMode === mode);
        if (!button) return;

        // 分段按钮激活态
        modeButtons.forEach((b) => {
          const isActive = b === button;
          b.classList.toggle('is-active', isActive);
          b.setAttribute('aria-selected', String(isActive));
        });

        // 联动面板显隐
        this.querySelectorAll('[data-klaviyo-panel]').forEach((panel) => {
          panel.hidden = panel.dataset.klaviyoPanel !== mode;
        });

        // 更新提交后行为：跳转 variant / source（数量固定为 1）
        if (button.dataset.modeVariant) {
          this.dataset.redirectVariant = button.dataset.modeVariant;
        } else {
          delete this.dataset.redirectVariant;
        }
        if (button.dataset.modeSource) {
          this.dataset.source = button.dataset.modeSource;
        }

        // 更新按钮文案
        if (button.dataset.modeButtonLabel && this.#submitButton) {
          this.#submitButton.textContent = button.dataset.modeButtonLabel;
        }

        // 切换模式时清除上一次的状态提示
        this.#clearStatus();
      };

      modeButtons.forEach((button) => {
        button.addEventListener(
          'click',
          () => activateMode(button.dataset.klaviyoMode),
          { signal: this.#abortController.signal }
        );
      });

      // 初始化：默认激活标记了 is-active 的按钮，否则第一个
      const initial = modeButtons.find((b) => b.classList.contains('is-active')) || modeButtons[0];
      activateMode(initial.dataset.klaviyoMode);
    }

    disconnectedCallback() {
      this.#abortController?.abort();
    }

    #handleSubmit = async (event) => {
      event.preventDefault();

      if (!this.#emailInput.checkValidity()) {
        this.#emailInput.reportValidity();
        return;
      }

      const companyId = this.dataset.companyId;
      const listId = this.dataset.listId;
      const email = this.#emailInput.value.trim();

      if (!companyId || !listId) {
        this.#setStatus('error', '订阅服务尚未配置，请稍后再试。');
        console.error('SpxKlaviyoApi: 缺少 data-company-id 或 data-list-id。');
        return;
      }

      const redirectUrl = this.#buildCheckoutUrl(email);
      this.#setLoading(true);

      try {
        const response = await fetch(
          `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(companyId)}`,
          {
            method: 'POST',
            headers: {
              Accept: 'application/vnd.api+json',
              'Content-Type': 'application/vnd.api+json',
              revision: this.dataset.revision || API_REVISION,
            },
            body: JSON.stringify(this.#createPayload(listId, email)),
          }
        );

        if (!response.ok) {
          throw new Error(`Klaviyo subscription request failed: ${response.status}`);
        }

        const message = this.dataset.successMessage || '感谢订阅！';
        this.#setStatus('success', message);
        this.#form.reset();
        this.dispatchEvent(
          new CustomEvent('klaviyo:success', {
            bubbles: true,
            detail: { email },
          })
        );

        if (redirectUrl) {
          window.location.assign(redirectUrl);
          return;
        }
      } catch (error) {
        console.error('SpxKlaviyoApi:', error);
        this.#setStatus('error', this.dataset.errorMessage || '订阅失败，请稍后再试。');
        this.dispatchEvent(
          new CustomEvent('klaviyo:error', {
            bubbles: true,
            detail: { error },
          })
        );
      } finally {
        this.#setLoading(false);
      }
    };

    #buildCheckoutUrl(email) {
      const variantId = this.dataset.redirectVariant;
      if (!variantId) return '';

      return `${window.Shopify?.routes?.root || '/'}cart/${variantId}:1?checkout[email]=${encodeURIComponent(email)}`
        .replace('//cart', '/cart');
    }

    #createPayload(listId, email) {
      const profileAttributes = {
        email,
        subscriptions: {
          email: {
            marketing: {
              consent: 'SUBSCRIBED',
            },
          },
        },
      };

      if (this.dataset.profileTag) {
        profileAttributes.properties = { tag: this.dataset.profileTag };
      }

      return {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: this.dataset.source || 'Shopify newsletter',
            profile: {
              data: {
                type: 'profile',
                attributes: profileAttributes,
              },
            },
          },
          relationships: {
            list: {
              data: {
                type: 'list',
                id: listId,
              },
            },
          },
        },
      };
    }

    #setLoading(isLoading) {
      if (isLoading) {
        this.dataset.state = 'loading';
      } else if (this.dataset.state === 'loading') {
        this.dataset.state = 'idle';
      }

      this.setAttribute('aria-busy', String(isLoading));

      if (this.#submitButton instanceof HTMLButtonElement) {
        this.#submitButton.disabled = isLoading;
      }
    }

    #setStatus(state, message) {
      this.dataset.state = state;

      if (this.#status instanceof HTMLElement) {
        this.#status.hidden = false;
        this.#status.dataset.state = state;
        this.#status.textContent = message;
      }
    }

    #clearStatus() {
      delete this.dataset.state;

      if (this.#status instanceof HTMLElement) {
        this.#status.hidden = true;
        delete this.#status.dataset.state;
        this.#status.textContent = '';
      }
    }
  }

  if (!customElements.get(COMPONENT_NAME)) {
    customElements.define(COMPONENT_NAME, SpxKlaviyoApi);
  }
})();
